import React, { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import TopBar from "@/components/TopBar";
import { Badge } from "@/components/DataBits";
import RiskBanner from "@/components/RiskBanner";
import Footer from "@/components/Footer";
import StockStats from "@/components/stock/StockStats";
import StockCharts from "@/components/stock/StockCharts";
import OptionsChain from "@/components/stock/OptionsChain";
import StrategyPanel from "@/components/stock/StrategyPanel";
import { ArrowLeft, Sparkle, Star } from "@phosphor-icons/react";
import { toast } from "sonner";

function signalBorder(severity) {
  if (severity === "positive") return "border-[#00e676] text-[#00e676]";
  if (severity === "negative") return "border-[#ff3b30] text-[#ff3b30]";
  return "border-[#ffcc00] text-[#ffcc00]";
}

export default function StockDetail() {
  const { symbol } = useParams();
  const nav = useNavigate();
  const sym = (symbol || "").toUpperCase();
  const [momentum, setMomentum] = useState(null);
  const [history, setHistory] = useState([]);
  const [options, setOptions] = useState(null);
  const [exp, setExp] = useState(null);
  const [tab, setTab] = useState("calls");
  const [insight, setInsight] = useState(null);
  const [insightLoading, setInsightLoading] = useState(false);
  const [watchlist, setWatchlist] = useState([]);
  const [period, setPeriod] = useState("3mo");
  const [stratOpen, setStratOpen] = useState(false);
  const [stratRisk, setStratRisk] = useState("moderate");
  const [stratOutlook, setStratOutlook] = useState("auto");
  const [strategy, setStrategy] = useState(null);
  const [stratLoading, setStratLoading] = useState(false);
  const [volumePref, setVolumePref] = useState("standard");

  const generateStrategy = async () => {
    setStratLoading(true); setStrategy(null);
    try {
      const r = await api.post(`/strategy/${sym}`, { risk: stratRisk, outlook: stratOutlook, volume_pref: volumePref });
      setStrategy(r.data.strategy);
    } catch (e) {
      const detail = e?.response?.data?.detail || "Failed";
      toast.error(detail);
      if (e?.response?.status === 402) nav("/upgrade");
    } finally { setStratLoading(false); }
  };

  const load = useCallback(async () => {
    const results = await Promise.allSettled([
      api.get(`/momentum/${sym}`),
      api.get(`/history/${sym}?period=${period}`),
      api.get(`/options/${sym}`),
      api.get("/watchlist"),
    ]);
    const [mRes, hRes, oRes, wRes] = results;
    if (mRes.status === "fulfilled") setMomentum(mRes.value.data);
    else { toast.error("No momentum data for " + sym); return; }
    if (hRes.status === "fulfilled") setHistory(hRes.value.data.data);
    else setHistory([]);
    if (oRes.status === "fulfilled") {
      setOptions(oRes.value.data);
      setExp(oRes.value.data.expiration);
    } else {
      setOptions({ expirations: [], calls: [], puts: [] });
    }
    if (wRes.status === "fulfilled") setWatchlist(wRes.value.data.symbols);
  }, [sym, period]);

  useEffect(() => { load(); }, [load]);

  const loadOptions = async (newExp) => {
    setExp(newExp);
    const r = await api.get(`/options/${sym}?expiration=${newExp}`);
    setOptions(r.data);
  };

  const getInsight = async () => {
    setInsightLoading(true);
    try {
      const r = await api.get(`/insights/${sym}`, { params: { volume_pref: volumePref } });
      setInsight(r.data);
    } catch {
      toast.error("Insight failed");
    } finally { setInsightLoading(false); }
  };

  const inWatch = watchlist.includes(sym);
  const toggleWatch = async () => {
    try {
      const r = inWatch
        ? await api.delete(`/watchlist/${sym}`)
        : await api.post(`/watchlist`, { symbol: sym });
      setWatchlist(r.data.symbols);
    } catch { toast.error("Error"); }
  };

  if (!momentum) {
    return (
      <div className="min-h-screen bg-[#050505] text-white">
        <TopBar />
        <div className="p-12 text-center font-mono-data text-[#555] text-xs">LOADING {sym}…</div>
      </div>
    );
  }

  const m = momentum;
  const watchClass = inWatch
    ? "border-[#00e676] text-[#00e676]"
    : "border-[#222] text-[#8a8a8e] hover:text-white";

  return (
    <div className="min-h-screen bg-[#050505] text-white">
      <TopBar />
      <div className="px-6 py-5">
        {/* Header */}
        <div className="flex items-center justify-between mb-5">
          <div className="flex items-center gap-4">
            <button onClick={() => nav(-1)} className="text-[#8a8a8e] hover:text-white" data-testid="back-btn">
              <ArrowLeft size={18}/>
            </button>
            <div>
              <div className="flex items-baseline gap-3">
                <h1 className="font-heading text-4xl tracking-tight">{sym}</h1>
                {m.optionable && <Badge variant="positive">OPTIONABLE</Badge>}
              </div>
              <div className="flex items-baseline gap-4 mt-1">
                <span className="font-mono-data text-2xl">${m.price.toFixed(2)}</span>
                <span className={`font-mono-data text-base ${m.change_1d >= 0 ? "pos" : "neg"}`}>
                  {m.change_1d >= 0 ? "+" : ""}{m.change_1d.toFixed(2)}%
                </span>
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <select
              value={volumePref}
              onChange={e => setVolumePref(e.target.value)}
              data-testid="volume-pref"
              title="How AI should weight volume in analysis & strategy"
              className="px-2 py-1.5 bg-[#0a0a0a] border border-[#222] hover:border-[#444] text-xs font-mono-data uppercase tracking-wider text-[#8a8a8e] outline-none"
            >
              <option value="ignore">VOL · IGNORE</option>
              <option value="standard">VOL · STANDARD</option>
              <option value="volume_driven">VOL · DRIVEN</option>
              <option value="surge_only">VOL · SURGE ONLY</option>
            </select>
            <button
              onClick={toggleWatch}
              className={`flex items-center gap-1 px-3 py-1.5 border text-xs font-mono-data uppercase tracking-wider ${watchClass}`}
              data-testid="detail-watch-toggle"
            >
              <Star size={14} weight={inWatch ? "fill" : "regular"} /> {inWatch ? "WATCHING" : "WATCH"}
            </button>
            <button
              onClick={() => setStratOpen(s => !s)}
              data-testid="strategy-toggle"
              className="flex items-center gap-1 px-3 py-1.5 border border-[#f2ce72] text-[#f2ce72] hover:bg-[#f2ce72] hover:text-black text-xs font-mono-data uppercase tracking-wider"
            >
              <Sparkle size={14} weight="bold"/> STRATEGY
            </button>
            <button
              onClick={getInsight}
              disabled={insightLoading}
              className="flex items-center gap-1 px-3 py-1.5 bg-[#f2ce72] text-black text-xs font-mono-data uppercase tracking-wider hover:bg-white disabled:opacity-50"
              data-testid="ai-insight-btn"
            >
              <Sparkle size={14} weight="bold"/> {insightLoading ? "ANALYZING…" : "AI INSIGHT"}
            </button>
          </div>
        </div>

        <StockStats m={m} />

        {/* AI Insight */}
        {insight && (
          <div className="mb-4" data-testid="ai-insight-panel">
            <RiskBanner variant="ai" />
            <div className="ai-panel p-4 mt-2">
              <div className="micro-label text-[#f2ce72] mb-2">// AI INSIGHT · {insight.model}</div>
              <p className="text-sm leading-relaxed whitespace-pre-wrap">{insight.insight}</p>
            </div>
          </div>
        )}

        {stratOpen && (
          <StrategyPanel
            risk={stratRisk}
            outlook={stratOutlook}
            loading={stratLoading}
            strategy={strategy}
            onRiskChange={setStratRisk}
            onOutlookChange={setStratOutlook}
            onGenerate={generateStrategy}
          />
        )}

        {/* Signals */}
        {m.signals?.length > 0 && (
          <div className="panel p-4 mb-4">
            <div className="micro-label mb-3">/ active signals</div>
            <div className="flex flex-wrap gap-2">
              {m.signals.map((s) => (
                <div
                  key={s.type}
                  className={`flex items-center gap-2 px-3 py-1.5 border-l-2 bg-[#0a0a0a] text-xs font-mono-data ${signalBorder(s.severity)}`}
                  data-testid={`signal-${s.type}`}
                >
                  <span>{s.message}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        <StockCharts history={history} period={period} onPeriodChange={setPeriod} />

        <OptionsChain
          options={options}
          exp={exp}
          tab={tab}
          onExpChange={loadOptions}
          onTabChange={setTab}
        />
      </div>
      <Footer />
    </div>
  );
}
